import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { fundingAuthAPI, resolveIpeLoginErrorMessage } from '@/utils/api';
import ClickCaptchaModal, { IPE_USER_SMS_CAPTCHA_SCENE } from '@/components/captcha/ClickCaptchaModal';
import IpeSmsCodeField, { phonePattern } from './IpeSmsCodeField';
import { buildLoginContext, prefetchLoginContext } from '@/utils/deviceLocation';
import { showToast } from '@/utils/toast';

function isCaptchaRequiredError(err) {
  return err?.code === 'CAPTCHA_REQUIRED';
}

/** 微信登录暂未开放，改为 true 可恢复 Tab */
const WECHAT_LOGIN_VISIBLE = false;

const LOGIN_MODE_IDS = [
  ...(WECHAT_LOGIN_VISIBLE ? ['wechat'] : []),
  'password',
  'sms',
];

function LoginTermsAgreement({ checked, onChange, disabled = false, className = '' }) {
  const { t } = useTranslation('login');
  return (
    <label className={`ipe-login-form__terms${className ? ` ${className}` : ''}`}>
      <input
        type="checkbox"
        className="ipe-login-form__terms-checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="ipe-login-form__terms-text">
        {t('auth.agreePrefix')}
        <a
          href="/legal/terms"
          className="ipe-login-form__terms-link"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {t('auth.terms')}
        </a>
        {t('auth.agreeAnd')}
        <a
          href="/legal/privacy"
          className="ipe-login-form__terms-link"
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {t('auth.privacy')}
        </a>
      </span>
    </label>
  );
}

function WeChatLoginPanel({ onWeChatSuccess, submitting }) {
  const { t } = useTranslation('login');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fundingAuthAPI.getWeChatLoginSession()
      .then((data) => {
        if (cancelled) return;
        setSession(data || null);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || t('auth.wechatLoadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (!session?.enabled || !session?.state) return undefined;

    const poll = async () => {
      try {
        const result = await fundingAuthAPI.pollWeChatLogin(session.state);
        if (result?.status === 'success' && result?.token) {
          window.clearInterval(pollRef.current);
          pollRef.current = null;
          await onWeChatSuccess(result.token);
        } else if (result?.status === 'expired') {
          window.clearInterval(pollRef.current);
          pollRef.current = null;
          setError(t('auth.wechatExpired'));
        }
      } catch {
        /* ignore transient poll errors */
      }
    };

    pollRef.current = window.setInterval(poll, 2000);
    poll();
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [onWeChatSuccess, session?.enabled, session?.state, t]);

  if (loading) {
    return <p className="ipe-auth-modal__wechat-hint">{t('auth.wechatLoading')}</p>;
  }

  if (!session?.enabled) {
    return (
      <div className="ipe-auth-modal__wechat-disabled">
        <p>{session?.message || t('auth.wechatClosed')}</p>
      </div>
    );
  }

  return (
    <div className="ipe-auth-modal__wechat-panel">
      <iframe
        title={t('auth.wechatFrame')}
        className="ipe-auth-modal__wechat-frame"
        src={session.qrConnectUrl}
      />
      <p className="ipe-auth-modal__wechat-hint">{t('auth.wechatHint')}</p>
      {error ? <p className="ipe-auth-modal__error" role="alert">{error}</p> : null}
      {submitting ? <p className="ipe-auth-modal__wechat-hint">{t('auth.loggingIn')}</p> : null}
    </div>
  );
}

export default function IpeLoginForm({
  onSubmit,
  onSmsSubmit,
  onWeChatSuccess,
  onSendVerificationCode,
  submitting = false,
  variant = 'default',
  onSwitchToRegister,
  onForgotPassword,
}) {
  const [loginMode, setLoginMode] = useState('password');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [smsCaptchaRequired, setSmsCaptchaRequired] = useState(false);
  const [smsCaptchaToken, setSmsCaptchaToken] = useState('');
  const [smsCaptchaModalOpen, setSmsCaptchaModalOpen] = useState(false);
  const phoneInputRef = useRef(null);
  const passwordSubmitInFlightRef = useRef(false);

  const { t } = useTranslation('login');
  const isModal = variant === 'modal';
  const loginModes = LOGIN_MODE_IDS.map((id) => ({
    id,
    label: t(id === 'wechat' ? 'auth.wechatTab' : id === 'sms' ? 'auth.smsTab' : 'auth.passwordTab'),
  }));

  const handleAgreedToTermsChange = (checked) => {
    setAgreedToTerms(checked);
    if (checked) {
      prefetchLoginContext();
    }
  };

  useEffect(() => {
    if (!isModal) return undefined;
    const node = phoneInputRef.current;
    if (!node) return undefined;

    const forceInputBg = () => {
      node.style.setProperty('background-color', '#f7f4f0', 'important');
      node.style.setProperty('background', '#f7f4f0', 'important');
    };

    forceInputBg();
    const timers = [0, 50, 150, 400].map((ms) => window.setTimeout(forceInputBg, ms));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [isModal, loginMode]);

  useEffect(() => {
    if (loginMode !== 'sms') {
      setSmsCaptchaRequired(false);
      setSmsCaptchaToken('');
      setSmsCaptchaModalOpen(false);
    }
  }, [loginMode]);

  const openSmsCaptchaGate = useCallback(() => {
    setSmsCaptchaRequired(true);
    setSmsCaptchaToken('');
    setSmsCaptchaModalOpen(true);
  }, []);

  const handleCaptchaRequiredError = useCallback((err) => {
    if (!isCaptchaRequiredError(err)) {
      return false;
    }
    openSmsCaptchaGate();
    setError(err.message || t('auth.captchaFirst'));
    return true;
  }, [openSmsCaptchaGate, t]);

  const handleSendVerificationCode = useCallback(async (p, scene) => {
    // captcha-required 仍在 Redis、本地无有效 token（含过期）时，点发码先弹人机窗
    if (smsCaptchaRequired && !smsCaptchaToken) {
      openSmsCaptchaGate();
      setError(t('auth.captchaContinue'));
      const err = new Error(t('auth.captchaFirst'));
      err.code = 'CAPTCHA_REQUIRED';
      throw err;
    }
    try {
      const data = await onSendVerificationCode(
        p,
        scene,
        smsCaptchaRequired ? smsCaptchaToken || undefined : undefined,
      );
      if (smsCaptchaToken) {
        setSmsCaptchaToken('');
        setSmsCaptchaRequired(false);
      }
      return data;
    } catch (err) {
      if (handleCaptchaRequiredError(err)) {
        throw err;
      }
      throw err;
    }
  }, [
    handleCaptchaRequiredError,
    onSendVerificationCode,
    openSmsCaptchaGate,
    smsCaptchaRequired,
    smsCaptchaToken,
    t,
  ]);

  const handleCaptchaVerified = useCallback((token) => {
    setSmsCaptchaToken(token);
    setError('');
    showToast(t('auth.captchaPassedToast'), 'success');
  }, [t]);

  const formClass = ['ipe-login-form', isModal ? 'ipe-login-form--modal' : ''].filter(Boolean).join(' ');

  const ensureTerms = () => {
    if (!agreedToTerms) {
      setError(t('auth.needTerms'));
      return null;
    }
    return buildLoginContext(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordSubmitInFlightRef.current || submitting) {
      return;
    }
    setError('');
    const loginContext = ensureTerms();
    if (!loginContext) return;
    passwordSubmitInFlightRef.current = true;
    try {
      await onSubmit(phone, password, loginContext);
    } catch (err) {
      setError(resolveIpeLoginErrorMessage(err, t('auth.loginFailed')));
    } finally {
      passwordSubmitInFlightRef.current = false;
    }
  };

  const handleWeChatSuccess = async (token) => {
    setError('');
    const loginContext = ensureTerms();
    if (!loginContext) return;
    try {
      await onWeChatSuccess(token, loginContext);
    } catch (err) {
      setError(err?.message || t('auth.wechatFailed'));
    }
  };

  const handleSmsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const loginContext = ensureTerms();
    if (!loginContext) return;
    if (!phonePattern.test(String(phone || '').trim())) {
      setError(t('auth.badPhone'));
      return;
    }
    if (!/^\d{6}$/.test(String(smsCode || '').trim())) {
      setError(t('auth.badCode'));
      return;
    }
    try {
      await onSmsSubmit(
        phone.trim(),
        smsCode.trim(),
        loginContext,
      );
      setSmsCaptchaToken('');
      setSmsCaptchaRequired(false);
    } catch (err) {
      setError(resolveIpeLoginErrorMessage(err, t('auth.loginFailed')));
    }
  };

  if (isModal) {
    return (
      <div className="ipe-auth-modal__form-area">
        <div className="ipe-auth-modal__form-body">
          <h2 id="ipe-auth-modal-title" className="ipe-auth-modal__form-title">
            {t('auth.welcome')}
          </h2>
          <p className="ipe-auth-modal__form-subtitle">
            {t('auth.welcomeSub')}
          </p>

          <div className="ipe-auth-modal__login-tabs" role="tablist" aria-label={t('auth.methods')}>
            {loginModes.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={loginMode === item.id}
                className={`ipe-auth-modal__login-tab${loginMode === item.id ? ' ipe-auth-modal__login-tab--active' : ''}`}
                onClick={() => {
                  setLoginMode(item.id);
                  setError('');
                }}
              >
                {item.label}
              </button>
            ))}
          </div>

          {loginMode === 'wechat' ? (
            <>
              <LoginTermsAgreement
                checked={agreedToTerms}
                onChange={handleAgreedToTermsChange}
                disabled={submitting}
                className="ipe-login-form__terms--modal"
              />
              <WeChatLoginPanel onWeChatSuccess={handleWeChatSuccess} submitting={submitting} />
            </>
          ) : null}

          {loginMode === 'password' ? (
            <form className={formClass} onSubmit={handlePasswordSubmit} autoComplete="off">
              <div className="ipe-auth-modal__field">
                <input
                  ref={phoneInputRef}
                  id="ipe-login-phone"
                  className="ipe-login-form__input"
                  type="text"
                  inputMode="tel"
                  name="ipe-login-phone"
                  autoComplete="off"
                  placeholder={t('auth.phone')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  maxLength={11}
                  disabled={submitting}
                />
              </div>
              <div className="ipe-auth-modal__field ipe-auth-modal__field--password">
                <input
                  id="ipe-login-password"
                  className="ipe-login-form__input"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder={t('auth.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="ipe-login-form__password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {error ? (
                <p className="ipe-auth-modal__error" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="ipe-auth-modal__terms-row">
                <LoginTermsAgreement
                  checked={agreedToTerms}
                  onChange={handleAgreedToTermsChange}
                  disabled={submitting}
                  className="ipe-login-form__terms--modal"
                />
                <button
                  type="button"
                  className="ipe-auth-modal__link ipe-auth-modal__link--standalone"
                  onClick={onForgotPassword}
                >
                  {t('auth.forgot')}
                </button>
              </div>

              <button type="submit" className="ipe-auth-modal__submit" disabled={submitting}>
                {submitting ? t('auth.loggingIn') : t('auth.loginNow')}
              </button>
            </form>
          ) : null}

          {loginMode === 'sms' ? (
            <form className={formClass} onSubmit={handleSmsSubmit} autoComplete="off">
              <div className="ipe-auth-modal__field">
                <input
                  ref={phoneInputRef}
                  id="ipe-login-phone-sms"
                  className="ipe-login-form__input"
                  type="tel"
                  inputMode="tel"
                  placeholder={t('auth.phone')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  maxLength={11}
                  disabled={submitting}
                />
              </div>

              <IpeSmsCodeField
                phone={phone}
                scene="login"
                value={smsCode}
                onChange={setSmsCode}
                onSendCode={handleSendVerificationCode}
                disabled={submitting}
              />

              {smsCaptchaRequired && !smsCaptchaToken ? (
                <p className="ipe-auth-modal__sms-hint" role="status">
                  {t('auth.smsTooMany')}
                  <button
                    type="button"
                    className="ipe-auth-modal__link"
                    onClick={() => setSmsCaptchaModalOpen(true)}
                  >
                    {t('auth.smsVerifyFirst')}
                  </button>
                </p>
              ) : null}

              {smsCaptchaToken ? (
                <p className="ipe-auth-modal__sms-hint ipe-auth-modal__sms-hint--ok" role="status">
                  {t('auth.smsReady')}
                </p>
              ) : null}

              {error ? (
                <p className="ipe-auth-modal__error" role="alert">
                  {error}
                </p>
              ) : null}

              <LoginTermsAgreement
                checked={agreedToTerms}
                onChange={handleAgreedToTermsChange}
                disabled={submitting}
                className="ipe-login-form__terms--modal"
              />

              <button type="submit" className="ipe-auth-modal__submit" disabled={submitting}>
                {submitting ? t('auth.loggingIn') : t('auth.loginNow')}
              </button>
            </form>
          ) : null}
        </div>

        <ClickCaptchaModal
          open={smsCaptchaModalOpen}
          scene={IPE_USER_SMS_CAPTCHA_SCENE}
          dismissOnOverlay={!smsCaptchaRequired}
          onClose={() => setSmsCaptchaModalOpen(false)}
          onVerified={handleCaptchaVerified}
          onError={(message) => setError(message || t('auth.captchaFailed'))}
        />

        <p className="ipe-auth-modal__switch">
          {t('auth.noAccount')}
          <button type="button" className="ipe-auth-modal__switch-btn" onClick={onSwitchToRegister}>
            {t('auth.freeRegister')}
          </button>
        </p>
      </div>
    );
  }

  return (
    <form className={formClass} onSubmit={handlePasswordSubmit} autoComplete="off">
      <div className="ipe-login-form__field">
        <label htmlFor="ipe-login-phone">
          {t('auth.account')}<span className="ipe-login-form__required" aria-hidden="true">*</span>
        </label>
        <div className="ipe-login-form__input-shell">
          <input
            ref={phoneInputRef}
            id="ipe-login-phone"
            className="ipe-login-form__input"
            type="text"
            inputMode="tel"
            name="ipe-login-phone"
            autoComplete="off"
            placeholder={t('auth.phone')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={11}
            disabled={submitting}
          />
        </div>
      </div>
      <div className="ipe-login-form__field">
        <label htmlFor="ipe-login-password">
          {t('auth.passwordLabel')}<span className="ipe-login-form__required" aria-hidden="true">*</span>
        </label>
        <div className="ipe-login-form__password-wrap">
          <input
            id="ipe-login-password"
            className="ipe-login-form__input"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
          />
          <button
            type="button"
            className="ipe-login-form__password-toggle"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
      {error ? (
        <p className="ipe-login-form__error" role="alert">
          {error}
        </p>
      ) : null}
      <LoginTermsAgreement
        checked={agreedToTerms}
        onChange={handleAgreedToTermsChange}
        disabled={submitting}
        className="ipe-login-form__terms--page"
      />
      <button type="submit" className="ipe-login-form__submit" disabled={submitting}>
        <span>{submitting ? t('auth.loggingIn') : t('auth.login')}</span>
      </button>
    </form>
  );
}
